import { FollowUpInput } from '../../components/FollowUpInput.js';
import { savedSignatures } from '../../lib/savedSignature.js';
import { RecipientChips } from './RecipientChips.js';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, Eye, FileText, Link2, Plus, Save, SlidersHorizontal, Upload } from 'lucide-react';
import type { DigitalDocument, SignatureAnchor, SignatureDocument, LibraryDocument, Paginated } from '@worki/shared';
import { digitalDocumentSchema, LIMITS, SIGNATURE_FOLLOW_UP_HOURS, SIGNATURE_VALID_DAYS } from '@worki/shared';
import { api, authedFetch, RequestError } from '../../lib/api.js';
import * as platform from '@platform';
import { currentLocale } from '../../i18n.js';
import { cn } from '../../lib/cn.js';
import { Button, ErrorNotice, Field, Input, Select } from '../../components/ui.js';
import { DocumentTitleField } from './DocumentTitleField.js';
import { deviceStore, SenderSignatureDialog } from '../../components/PlaceDialogs.js';
import { senderSignatureOf, signAsSender, removeSignerAnchors } from '../../lib/anchors.js';
import { mergeSenderAnchors, scanSenderAnchors } from '../../lib/senderAnchorDetection.js';
import { useIsPhone } from '../../lib/useIsPhone.js';
import { AnchoredMenu } from '../../components/AnchoredMenu.js';
import { ConfirmDialog, ModalShell } from '../../components/Modal.js';
import { composeCanKeep, composeHasWork, composeReady } from './composeWork.js';
import { whatsappHandOff } from './whatsappHandOff.js';
import { shrinkPicked } from '../../components/QuickScan.js';
import { useToast } from '../../components/Toast.js';
import { DigitalDocumentPreview } from './DigitalDocumentPreview.js';
import { useAuthenticated } from '../../lib/session.js';
import { DigitalDocumentBuilder } from './DigitalDocumentBuilder.js';
import { digitalDocumentForCopy } from './digitalDocumentModel.js';
import { DocumentPages, FILE_ACCEPT, isWordFile, PickedFiles, postFile, titleFromFile } from './parts.js';

/**
 * SIG-004 — a document to sign, made in one screen.
 *
 * The old flow asked the same question twice in two modals with a list page in
 * between, because the placement panel drew a file the server was holding: a
 * row had to exist before anyone could tap a page, so the row was created
 * first and the file placed second.
 *
 * Nothing about that was a decision. The placer draws whatever bytes it is
 * given, and the picked file is already here — so the pages can be drawn, and
 * the boxes placed, before anything is uploaded. The document is then created,
 * filled, placed and sent in one request, and nothing at all is written if the
 * sender walks away.
 *
 * The screen reads in the order the sender thinks in: what it is called, then
 * the file itself, then the pages it is marked on, then who signs, then how it
 * goes out. The pages sit with the file rather than after the channels, because
 * they are not a field to fill in — they are the document, and marking one
 * before choosing how to send it is the only order that works. Who signs is
 * under the pages, not between them and the file (1.24); a digital document has
 * no pages, so there the same card is its delivery, right after the builder.
 */

/**
 * What may be picked. Wider than the placement panel's own accept list: several
 * files become one document here by way of the scan merge, which takes
 * photographs as readily as PDFs — and most documents that need signing were
 * photographed rather than exported.
 */
const COMPOSE_ACCEPT = `${FILE_ACCEPT},image/jpeg,image/png`;

interface Delivery {
  link: string;
  channel: 'email' | 'whatsapp';
  delivered: boolean;
  error?:string;
  deepLink?: string;
  /** SIG-003 — which of the signers this went to, on a document with several. */
  signer?: number;
  signerName?: string;
}

/** Everybody who signs. The first of them is who the document is sent to. */
interface Signer {
  names?:string[];phones?:string[];emails?:string[];
  name: string;
  phone: string;
  email: string;
}

const EMPTY_SIGNER: Signer = { name: '', phone: '', email: '' };

interface ComposeResult extends Delivery {
  document: SignatureDocument;
  /** One entry per channel it went out on, in the order they were chosen. */
  deliveries: Delivery[];
}

type Channel = 'email' | 'whatsapp';

const CHANNELS: readonly Channel[] = ['email', 'whatsapp'];

/**
 * 1.57 — the lengths the follow-up can be set to. The shared list begins with
 * `null`, the business's own threshold, and that one has no chip: it is what is
 * in force when none of these is on, and the line under them says so.
 */
const FOLLOW_UP_CHIPS = SIGNATURE_FOLLOW_UP_HOURS.filter((hours) => hours !== null);

/**
 * 1.23 — a filed document's file, named the way the server names it. The one
 * place it is fetched: for a single pick off the shelf (or `?library=`) and for
 * several ticked at once alike.
 *
 * Null when the server will not give it, so the caller can say so; a request
 * that cannot be made at all throws.
 */
async function fetchLibraryFile(id: string): Promise<File | null> {
  const response = await authedFetch(`/library/${id}/file`);
  if (!response.ok) return null;
  const name = decodeURIComponent(
    /filename\*=UTF-8''([^;]+)/.exec(response.headers.get('content-disposition') ?? '')?.[1] ?? 'document.pdf',
  );
  const bytes = await response.blob();
  return new File([bytes], name, { type: bytes.type || 'application/pdf' });
}

export function SigningCompose({ asDialog = false }: { asDialog?: boolean } = {}) {
  const { t } = useTranslation();
  const { business, user } = useAuthenticated();
  const [previewOpen, setPreviewOpen] = useState(false);
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const queryClient = useQueryClient();
  const toast = useToast();
  const locale = currentLocale();
  const he = locale === 'he';
  const nextNumber = useQuery({ queryKey: ['signatures', 'next-number'], queryFn: () => api.get<{ number: number }>('/signatures/next-number') });
  const library = useQuery({ queryKey: ['library', 'signing-picker'], queryFn: async () => {
    const items: LibraryDocument[] = [];
    for (let page = 1; ; page++) {
      const result = await api.get<Paginated<LibraryDocument>>(`/library?page=${page}&pageSize=100`);
      items.push(...result.items);
      if (items.length >= result.total || result.items.length === 0) return items;
    }
  } });
  /* §7 — the name box's computer layout, by the rule quick scan uses for its
     camera (`InvoicesPage.tsx`): not a phone-sized screen, and not the app,
     which is a phone or a tablet at any width. */
  const desktop = !(useIsPhone() || platform.handheld);
  const inputRef = useRef<HTMLInputElement>(null);
  const optionsRef = useRef<HTMLButtonElement>(null);
  /** Whether the next pick starts over or adds to what is already chosen. */
  const replacing = useRef(false);

  /** What the sender picked, kept so that adding a file re-merges the set. */
  const [picked, setPickedState] = useState<File[]>([]);
  /** What is picked right now, for a merge that finishes later to ask: a pick
      taken back while it was being put together must not come back with it.

      Written where the pick is made, not copied from `picked` on render: a lone
      PDF needs no merge, so it can finish before React has drawn the pick, and
      a pick that arrived after an `await` (one off the shelf) is not drawn
      synchronously. Read from the render, it still said nothing was picked, and
      the file was thrown away — the pages never came. */
  const pickedNow = useRef(picked);
  const setPicked = (files: File[]) => {
    pickedNow.current = files;
    setPickedState(files);
  };
  /** The PDF that will be uploaded — the picked file, or what it all became. */
  const [file, setFile] = useState<File | null>(null);
  const detectionFileNow = useRef(file); detectionFileNow.current = file;
  const [pageCount, setPageCount] = useState<number | null>(null);
  const [anchors, setAnchors] = useState<SignatureAnchor[]>([]);
  const anchorsNow = useRef(anchors); anchorsNow.current = anchors;
  const [senderDetection, setSenderDetection] = useState(false);
  const senderDetectionNow = useRef(senderDetection); senderDetectionNow.current = senderDetection;
  const [detectionBusy, setDetectionBusy] = useState(false);
  const [detectionStatus, setDetectionStatus] = useState<'found' | 'none' | 'failed' | null>(null);
  const [detectedCount, setDetectedCount] = useState(0);
  const [pendingSenderRegions, setPendingSenderRegions] = useState<{ file: File; anchors: SignatureAnchor[] } | null>(null);

  const [title, setTitle] = useState('');
  /* `?digital=1` arrives from the paperclip on "my documents": a document to
     write here rather than one brought. It is what the screen starts as and not
     something it is put into once it has drawn, so the file card is never
     there first. */
  const [digital, setDigital] = useState(() => params.get('digital') === '1');
  const [digitalDocument, setDigitalDocument] = useState<DigitalDocument>({ version: 1, blocks: [] });
  const [libraryId, setLibraryId] = useState('');
  useEffect(() => {
    let active = true;
    setPendingSenderRegions(null); setDetectionStatus(null); setDetectedCount(0);
    if (!senderDetection || digital || !file) { setDetectionBusy(false); return; }
    setDetectionBusy(true);
    void scanSenderAnchors(file).then(found => {
      if (!active || file !== detectionFileNow.current || !senderDetectionNow.current) return;
      const savedImage=senderSignatureOf(anchorsNow.current)??savedSignatures(deviceStore(),user.id)[0]??null;
      const merged = mergeSenderAnchors(anchorsNow.current, found,savedImage);
      setDetectedCount(merged.added); setDetectionStatus(found.length ? 'found' : 'none');
      if (!merged.added) return;
      if (savedImage) setAnchors(merged.anchors);
      else { setDetectionStatus(null); setPendingSenderRegions({ file, anchors: found }); }
    }).catch(() => { if (active) setDetectionStatus('failed'); })
      .finally(() => { if (active) setDetectionBusy(false); });
    return () => { active = false; };
  }, [senderDetection, file, digital]);

  /* SIG-003, 1.16 — everybody who signs, the recipient first. Signing is
     parallel: all of them get their link at once and the document closes when
     the last one signs.

     The recipient used to be three fields of their own above this list, which
     asked the same question twice in two shapes and let a document be sent
     with contact details typed into it and not one box placed on the page. */
  const [signers, setSigners] = useState<Signer[]>([]);
  const [format,setFormat] = useState<'link'|'pdf'|'both'>('link');
  const [channels, setChannels] = useState<Channel[]>(['email']);
  const [validDays, setValidDays] = useState<number>(LIMITS.signatureLinkTtlDays);
  const [followUpHours, setFollowUpHours] = useState<number | null>(null);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [leaving, setLeaving] = useState(false);
  /** 1.23 — files are coming from the shelf: not picked yet, and already being worked on. */
  const [fetching, setFetching] = useState(false);
  const [result, setResult] = useState<ComposeResult | null>(null);

  /** Word, laid out as pages. Creates no row and stores no bytes. */
  const toPdf = async (picked: File): Promise<File> => {
    const body = new FormData();
    body.append('file', picked, picked.name);

    const response = await authedFetch('/signatures/convert', { method: 'POST', body });
    if (!response.ok) {
      const payload = (await response.json().catch(() => null)) as {
        error?: { code?: string; fields?: Record<string, string> };
      } | null;
      throw new RequestError(response.status, payload?.error?.fields?.file ?? payload?.error?.code ?? 'errors.unknown');
    }

    const bytes = await response.blob();
    // Named after the file the sender picked, with the extension it now has:
    // the label they recognise, over bytes that are honestly a PDF.
    return new File([bytes], picked.name.replace(/\.docx$/i, '.pdf'), { type: 'application/pdf' });
  };

  /**
   * Everything picked, as one document the browser can draw and the signer can
   * be shown.
   *
   * Several files rather than one, because what has to be signed is regularly
   * not one file: a contract and its appendix, a permit photographed a page at
   * a time, a form that arrived as three scans. Sending those used to mean three
   * documents with three numbers and three links, and a signer who had to sign
   * three times.
   *
   * A lone PDF is left exactly as it is — no merge, no re-encode, the bytes the
   * sender chose. Anything else goes through the scan merge, which copies PDF
   * pages through and fits photographs onto A4; Word is laid out first, because
   * it is the one thing neither the merge nor the browser can read.
   *
   * 1.46 — A photograph goes up the size the camera's own do, not as it came off
   * the phone, and the merge is told not to read it: a second a page for words
   * nothing on this screen looks at.
   */
  const prepare = useMutation({
    mutationFn: async (files: File[]) => {
      const parts: File[] = [];
      for (const one of files) parts.push(isWordFile(one) ? await toPdf(one) : await shrinkPicked(one));

      if (parts.length === 1 && parts[0]!.type === 'application/pdf') return parts[0]!;

      const body = new FormData();
      body.append('read', 'false');
      for (const part of parts) body.append('pages', part, part.name);

      const response = await authedFetch('/scan/pdf', { method: 'POST', body });
      if (!response.ok) {
        const payload = (await response.json().catch(() => null)) as {
          error?: { code?: string; fields?: Record<string, string> };
        } | null;
        throw new RequestError(
          response.status,
          payload?.error?.fields?.files ?? payload?.error?.code ?? 'errors.unknown',
        );
      }

      const bytes = await response.blob();
      return new File([bytes], `${titleFromFile(parts[0]!.name)}.pdf`, { type: 'application/pdf' });
    },
    onSuccess: (merged, files) => {
      if (files !== pickedNow.current) return;
      // A different set of pages is a different document: what was marked on the
      // old layout cannot be carried onto a new one.
      setAnchors([]);
      setPageCount(null);
      setFile(merged);
    },
  });

  const pick = (files: File[], mode: 'add' | 'replace' = 'add') => {
    if (files.length === 0) return;
    setDigital(false);
    const all = mode === 'replace' ? files : [...picked, ...files];
    setPicked(all);
    setFile(null);
    // The files already have names; making the sender type one again is the kind
    // of question a form should answer for itself. Still editable, and left
    // alone once they have.
    setTitle((was) => (was.trim() ? was : titleFromFile(all[0]!.name)));
    prepare.mutate(all);
  };

  /**
   * 1.29 — the pick taken back: what was chosen, what it became and what was
   * marked on it, and the shelf it came from, down to the address.
   *
   * `?library=` goes by replacing the entry and not by adding one: the popup is
   * an entry its opener pushed, and leaving it steps back off that entry.
   */
  const clearPick = () => {
    setPicked([]);
    setFile(null);
    setAnchors([]);
    setPageCount(null);
    setLibraryId('');
    // A merge still running would otherwise bring its file back.
    prepare.reset();
    if (params.has('library')) {
      const next = new URLSearchParams(params);
      next.delete('library');
      setParams(next, { replace: true });
    }
  };

  /**
   * The X in the name box: the pick and the name it brought both go. The name
   * is in the box the X sits in, so leaving it there read as an X that did
   * nothing, and the next pick off the shelf had it to look past.
   */
  const cancelPick = () => {
    clearPick();
    setTitle('');
  };

  /**
   * 1.30 — one file out of the several: the rest are put together again by the
   * same merge that adding one runs. What was marked goes, because it was
   * points on pages that have moved, and the sender is told so. With none left
   * it is the screen as it opened.
   */
  const removeFile = (index: number) => {
    const rest = picked.filter((_, at) => at !== index);
    if (anchors.length > 0) toast.show(t('signing.fileRemovedBoxesCleared'));
    if (rest.length === 0) {
      clearPick();
      return;
    }
    setAnchors([]);
    pick(rest, 'replace');
  };

  /**
   * 1.23 — several filed documents at once: the ones ticked in the name box's
   * list. Each file is fetched the way a single pick fetches it, in the order
   * they were ticked, and all of them join what is picked through the merge that
   * adding a file runs: one document, one number, one link, one signing, with
   * each of them on the list of files above the pages.
   *
   * All or none: one that cannot be fetched stops the set before any of it is
   * merged, so a document is never made of some of what was asked for.
   *
   * A title that was typed stays. An empty one becomes the first ticked
   * document's name on the shelf, as a single pick takes it, and not its file
   * name, which is what `pick` would otherwise use. `libraryId` is left alone:
   * it is one document's address, and setting it would fetch one file.
   */
  const pickFromShelf = async (documents: LibraryDocument[]) => {
    const [first] = documents;
    if (!first) return;
    setFetching(true);
    try {
      const fetched = await Promise.all(documents.map((document) => fetchLibraryFile(document.id)));
      const files = fetched.filter((one): one is File => one !== null);
      if (files.length !== documents.length) {
        toast.error(t('errors.unknown'));
        return;
      }
      setTitle((was) => (was.trim() ? was : first.title));
      pick(files, 'add');
    } catch {
      toast.error(t('errors.network'));
    } finally {
      setFetching(false);
    }
  };

  /*
   * `?library=<id>` arrives from "my documents": a file the owner filed once and
   * is now sending. It is fetched and dropped into the picker rather than being
   * attached on the server, so the screen carries on exactly as it does for a
   * file just chosen — same preview, same placements, same send.
   */
  const fromLibrary = libraryId || params.get('library');
  useEffect(() => {
    if (!fromLibrary) return;
    let cancelled = false;

    void (async () => {
      const fetched = await fetchLibraryFile(fromLibrary);
      if (!fetched) {
        toast.error(t('errors.unknown'));
        return;
      }
      if (!cancelled) {
        const selected = library.data?.find((item) => item.id === fromLibrary);
        if (selected) setTitle(selected.title);
        pick([fetched], 'replace');
      }
    })().catch(() => toast.error(t('errors.network')));

    return () => {
      cancelled = true;
    };
  }, [fromLibrary]);

  const duplicateId = params.get('duplicate');
  useEffect(() => {
    if (!duplicateId) return;
    let cancelled = false;
    void (async () => {
      const source = await api.get<SignatureDocument>(`/signatures/${duplicateId}`);
      if (cancelled) return;
      setTitle(source.title);
      if (!source.hasSource) { setDigital(true); setDigitalDocument(digitalDocumentForCopy(source.digitalDocument, source.body)); return; }
      const response = await authedFetch(`/signatures/${duplicateId}/source.pdf`);
      if (!response.ok) throw new Error('source unavailable');
      const sourceFile = new File([await response.blob()], source.sourceName ?? `${source.title}.pdf`, { type: 'application/pdf' });
      if (cancelled) return;
      setPicked([sourceFile]); setFile(sourceFile); setAnchors(source.anchors);
    })().catch(() => toast.error(t('errors.unknown')));
    return () => { cancelled = true; };
  }, [duplicateId]);

  /*
   * The boxes decide how many people are being asked to sign.
   *
   * Pressing copy on a placed box makes the next signer's box, which is the
   * gesture the sender already reaches for when a form has two signature lines
   * on it — so a name field for that person appears here rather than having to
   * be found first. It only ever grows: taking a box away does not take away a
   * name that has already been typed.
   */
  const placedFor = anchors.reduce((most, anchor) => Math.max(most, anchor.signer ?? 1), 0);
  useEffect(() => {
    setSigners((was) =>
      was.length >= placedFor ? was : [...was, ...Array.from({ length: placedFor - was.length }, () => EMPTY_SIGNER)],
    );
  }, [placedFor]);

  const signerNames: Record<number, string> = Object.fromEntries(
    signers.map((signer, index) => [index + 1, signer.name.trim() || t('signing.signerNumber', { number: index + 1 })]),
  );

  const editSigner = (index: number, patch: Partial<Signer>) =>
    setSigners((was) =>
      /* The first row is drawn before anybody is on the list — the delivery
         fields of a digital document, and since 1.27 a document to sign's
         first signer — and typing into it is what makes signer 1. */
      was.length > index
        ? was.map((signer, at) => (at === index ? { ...signer, ...patch } : signer))
        : [...was, { ...EMPTY_SIGNER, ...patch }],
    );

  /* Their boxes go with them: a box pointing at a signer who is no longer on
     the document is a signature line nobody will ever be asked to fill. */
  const dropSigner = (index: number) => {
    const gone = index + 1;
    setSigners((was) => was.filter((_, at) => at !== index));
    setAnchors(was=>removeSignerAnchors(was,gone));
  };

  /* The channel sends to the first signer: everybody after them is handed their
     link by the sender, so their address is theirs to leave empty. */
  const recipient = signers[0] ?? EMPTY_SIGNER;
  const addressOn = (channel: Channel): string =>
    (channel === 'email' ? recipient.email : recipient.phone).trim();

  const toggleChannel = (channel: Channel) =>
    setChannels((was) => (was.includes(channel) ? was.filter((one) => one !== channel) : [...was, channel]));

  const compose = useMutation({
    /*
     * SIG-3.13 — `keeping` is the whole difference between putting the document
     * down and sending it. One request either way: everything below is what the
     * sender decided, and which of the two happens is the last line of it, so a
     * draft carries the file, the places and the second signers exactly as a
     * send does rather than being a thinner thing to be repaired later.
     */
    mutationFn: async (keeping: boolean = false) => {
      if (!digital && !file) throw new RequestError(400, 'errors.unknown');
      const body = new FormData();
      if (keeping) body.append('draft', '1');
      if (digital) body.append('digitalDocument', JSON.stringify(digitalDocumentSchema.parse(digitalDocument)));
      else if (file) body.append('file', file, file.name);
      body.append('title', title.trim());
      body.append('recipientName', recipient.name.trim());
      if(recipient.names)body.append('recipientNames',JSON.stringify(recipient.names));
      if(recipient.phones)body.append('recipientPhones',JSON.stringify(recipient.phones));
      if(recipient.emails)body.append('recipientEmails',JSON.stringify(recipient.emails));
      body.append('format',format);
      body.append('channels', channels.join(','));
      // Both addresses travel, whichever the channels are: the one that is not
      // being sent to is still how this document finds the customer it belongs
      // to, and how it is chased later.
      if (recipient.email.trim()) body.append('recipientEmail', recipient.email.trim());
      if (recipient.phone.trim()) body.append('recipientPhone', recipient.phone.trim());
      // Left out when it is the platform's own window, so the row says "the
      // default" rather than freezing today's number into it.
      if (validDays !== LIMITS.signatureLinkTtlDays) body.append('validDays', String(validDays));
      if (followUpHours !== null) body.append('followUpHours', String(followUpHours));
      body.append('anchors', JSON.stringify(digital ? [] : anchors));
      /* The first of them is the recipient, sent above. The rest travel as
         they always did — the server's second-signer list is unchanged. */
      if (signers.length > 1) {
        body.append(
          'additionalSigners',
          JSON.stringify(
            signers.slice(1).map((signer) => ({
              name: signer.name.trim(),
              names:signer.names,phones:signer.phones,emails:signer.emails,
              /* Empty is nowhere to send it, which is a link the sender passes
                 on by hand — not an address of "". */
              phone: signer.phone.trim() || null,
              email: signer.email.trim() || null,
            })),
          ),
        );
      }
      return postFile<ComposeResult>('/signatures', body);
    },
    onSuccess: (sent, keeping) => {
      /* Kept, not sent: there is nothing to report and nothing to hand over, so
         the popup closes, back to the screen it was opened from. */
      if (keeping) {
        toast.success(t('signing.draftKept'));
        leave();
        return;
      }
      if(sent.deliveries.length && sent.deliveries.every(delivery=>delivery.delivered)) {
        void queryClient.invalidateQueries({queryKey:['history']});
        navigate('/quotes',{replace:true});
        return;
      }
      setResult(sent);
      /*
       * On the link driver nothing has left this server: WhatsApp opens with the
       * message already typed and the sender presses send there. Opening it from
       * here is the send, from where they are standing — and saying "sent"
       * instead would be a claim about a message still sitting in a draft.
       */
      const handOff = whatsappHandOff(sent.deliveries);
      if (handOff) void platform.openExternal(handOff);
    },
  });

  const retryDelivery=useMutation({
    mutationFn:async(target:Delivery)=>{
      if(!result)throw new RequestError(400,'errors.validation');
      return api.post<{document:SignatureDocument;sends:Delivery[]}>(`/signatures/${result.document.id}/send`,{channels:[{channel:target.channel}],format,expectedRevision:result.document.contentRevision,retryTargets:[{signer:target.signer??1,channel:target.channel}]});
    },
    onSuccess:(sent,target)=>{
      const next=result?.deliveries.map(delivery=>delivery.signer===target.signer&&delivery.channel===target.channel?(sent.sends[0]??delivery):delivery)??[];
      if(next.length&&next.every(delivery=>delivery.delivered)){void queryClient.invalidateQueries({queryKey:['history']});navigate('/quotes',{replace:true});return;}
      setResult(was=>was?{...was,document:sent.document,deliveries:next}:was);
    },
    onError:cause=>toast.error(t(cause instanceof RequestError?cause.code:'errors.unknown')),
  });

  const preparing = prepare.isPending || fetching;
  const started = composeHasWork({
    picked,
    preparing,
    title,
    blocks: digitalDocument.blocks.length,
    signers,
    anchors: anchors.length,
  });
  /* Less than `ready` below asks for, and on purpose: a document being put down
     has no channel yet, so it has no address to be missing. */
  const keepable = composeCanKeep({
    file: Boolean(file),
    blocks: digital ? digitalDocument.blocks.length : 0,
    title,
    signers,
  });
  const ready = composeReady({
    document: digital ? digitalDocumentSchema.safeParse(digitalDocument).success : Boolean(file),
    title,
    signers,
    channels,
  });

  /* A digital document is filled in by the customer, so it has one signer and
     no boxes to place. It is the same first row, in the same card — shown
     alone, and introduced as where the link is sent rather than who signs.

     1.27 — and so is a document to sign, where an empty card used to say "add
     at least one signer". The row is drawn and not counted: until something is
     typed in it `signers` is empty, so nothing above takes it for somebody. */

  /*
   * SIG-3.12 — leaving does not fetch a list afresh: the list behind the popup
   * is still mounted, and a screen this steps back to reads its rows from the
   * cache. Neither would show a document that has just been sent, so both are
   * told what changed.
   *
   * 1.2 — and leaving goes back to the screen that opened the popup. It used to
   * push `/signing`, a page most senders never open: nothing on a phone links
   * to the signing list, so a document started from the send history closed
   * onto a second list of the same documents, and the back button from there
   * opened the form again. The popup is an entry its opener pushed, so leaving
   * is a step back off it.
   *
   * The router numbers the entries it makes (`idx` on `history.state`), and
   * above zero the one below is the app's own. At zero nothing of ours is below
   * it — a link opened directly, or one that passed through the sign-in, which
   * replaces its way here, so `location.key` alone cannot tell — and a step
   * back would leave the app. There the popup gives way in place to the list
   * it is drawn over, which is the send history (4.10), as for a new quote.
   */
  const leave = () => {
    void queryClient.invalidateQueries({ queryKey: ['signatures'] });
    void queryClient.invalidateQueries({ queryKey: ['history'] });
    const entry = (window.history.state as { idx?: unknown } | null)?.idx;
    if (typeof entry === 'number' && entry > 0) navigate(-1);
    else navigate('/quotes', { replace: true });
  };

  /**
   * SIG-3.13 — the one way out, and it asks first.
   *
   * A full screen had a single back arrow. A dialog has four ways out and three
   * of them are easy to hit by accident on a phone — the ✕, Escape, a tap on
   * the backdrop — so all four come through here rather than each deciding for
   * itself what leaving costs.
   */
  const requestLeave = () => (started ? setLeaving(true) : leave());
  const Back = locale === 'he' ? ArrowRight : ArrowLeft;

  /* --- what happened, once it has happened ----------------------------- */
  if (result) {
    const delivered = result.deliveries.filter((one) => one.delivered);
    /* One row per person, not per send: a document going out on both channels
       is two deliveries carrying the same link to the same signer, and the
       sender only ever needs to see the link once. A WhatsApp hand-off that is
       still waiting is kept over an email that has already gone, because that
       is the row with something left to press. */
    const links = [...new Map(result.deliveries.map((one) => [one.link, one])).values()].map((one) => ({
      ...one,
      ...(result.deliveries.find((other) => other.link === one.link && other.deepLink) ?? {}),
    }));
    /* 4.14b — the WhatsApp message still waiting to be sent, if there is one:
       the same one opened automatically after sending. */
    const handOff = whatsappHandOff(result.deliveries);
    const done = (
      <div
        className={cn(
          'animate-fade-up space-y-4',
          // Inside the sheet this is the scrolling part of the card, and the
          // padding the page used to take from the layout comes from here.
          asDialog ? 'flex-1 min-h-0 overflow-y-auto p-4' : 'max-w-2xl mx-auto',
        )}
      >
        <div className="glass-card rounded-xl p-5 space-y-4 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-success/10 text-success">
            {delivered.length > 0 ? <Check className="h-6 w-6" /> : <Link2 className="h-6 w-6" />}
          </div>

          <div className="space-y-1">
            <h2 className="text-lg font-semibold">{result.document.title}</h2>
            <p className="text-sm text-muted-foreground">{t('signing.numberBadge', { number: result.document.number })} · {new Intl.DateTimeFormat(he ? 'he-IL' : 'en-GB').format(new Date(result.document.createdAt))}</p>
            {/*
              Three different things can happen here and they must not read
              alike: a mail the server accepted, a WhatsApp message sitting in
              the sender's own app waiting to be sent, and a link that nobody
              was sent at all. With two channels chosen, both lines are true at
              once and both are shown.
            */}
            {delivered.length > 0 && (
              <p className="text-sm text-muted-foreground">
                {t('signing.deliveredTo', {
                  to: delivered.map((one) => addressOn(one.channel)).join(' · '),
                })}
              </p>
            )}
            {result.deliveries.some((one) => !one.delivered && one.channel === 'whatsapp' && one.deepLink) && (
              <p className="text-sm text-muted-foreground">{t('signing.whatsappReady')}</p>
            )}
            {delivered.length === 0 && !result.deliveries.some((one) => one.channel === 'whatsapp' || one.error) && (
              <p className="text-sm text-muted-foreground">{t('signing.linkReady')}</p>
            )}
          </div>

          <ul className="space-y-2 text-start">
            {result.deliveries.map((delivery,index)=><li key={index} className="rounded-lg border border-border p-3 flex flex-wrap items-center justify-between gap-2">
              <span className="text-sm">{delivery.signerName??recipient.name} · {t(delivery.channel==='email'?'signing.channelEmail':'signing.channelWhatsapp')}</span>
              <span className={`text-sm ${delivery.error?'text-destructive':'text-muted-foreground'}`}>{t(delivery.delivered?'signing.sent':delivery.deepLink?'signing.whatsappReady':delivery.error??'errors.upstream')}</span>
              {!delivery.delivered&&!delivery.deepLink&&<Button size="sm" loading={retryDelivery.isPending} onClick={()=>retryDelivery.mutate(delivery)}>{t('common.retry')}</Button>}
            </li>)}
          </ul>

          {/* One link, or one per person. A document four people sign has four
              links, and a single box showing the first signer's is the sender
              copying the same address to everybody — which is one signature,
              and three people told the document is already signed. */}
          {links.length > 1 ? (
            <div className="space-y-2 text-start">
              <span className="text-sm font-semibold">{t('signing.signerLinks')}</span>
              {links.map((one) => (
                <Field key={one.link} label={one.signerName ?? t('signing.signerNumber', { number: one.signer ?? 1 })}>
                  <div className="flex items-center gap-2">
                    <Input readOnly dir="ltr" value={one.link} onFocus={(event) => event.target.select()} />
                    <Button
                      variant="ghost"
                      onClick={() => {
                        void navigator.clipboard?.writeText(one.link).then(() => toast.show(t('common.copied')));
                      }}
                    >
                      {t('common.copy')}
                    </Button>
                    {/* The hand-off that could not be opened automatically:
                        only one WhatsApp window can be opened for the sender,
                        and the rest are a press each. */}
                    {one.deepLink && (
                      <Button variant="ghost" onClick={() => void platform.openExternal(one.deepLink!)}>
                        {t('signing.channelWhatsapp')}
                      </Button>
                    )}
                  </div>
                </Field>
              ))}
            </div>
          ) : (
            <Field label={t('signing.link')}>
              <Input readOnly dir="ltr" value={result.link} onFocus={(event) => event.target.select()} />
            </Field>
          )}

          <div className="flex flex-wrap items-center justify-center gap-2">
            {links.length <= 1 && (
              <Button
                variant="ghost"
                onClick={() => {
                  void navigator.clipboard?.writeText(result.link).then(() => toast.show(t('common.copied')));
                }}
              >
                {t('common.copy')}
              </Button>
            )}
            {/* 4.14b — WhatsApp again, as each signer's row offers it on a
                document with several. The screen says WhatsApp is ready, and
                when the automatic opening did not happen this is the way to it
                that is not copying the link out by hand. */}
            {links.length <= 1 && handOff && (
              <Button variant="ghost" onClick={() => void platform.openExternal(handOff)}>
                {t('signing.channelWhatsapp')}
              </Button>
            )}
            {/* 1.2 — named for what it does rather than for the signing list:
                it goes back to whatever opened the popup, and that is seldom
                the list — nothing on a phone links to it. */}
            <Button variant="primary" onClick={leave}>
              {t('common.close')}
            </Button>
          </div>
        </div>
      </div>
    );

    /* Nothing is at stake here — the document is sent — so this close is the
       plain one rather than the guarded one. */
    return asDialog ? (
      <ModalShell open label={result.document.title} onClose={leave} size="wide">
        {done}
      </ModalShell>
    ) : (
      done
    );
  }

  const screen = (
    <div
      className={cn(
        'animate-fade-up space-y-4',
        asDialog
          // The sheet is the card now, so this is only what scrolls inside it.
          // The width it used to set for itself is the sheet's to decide.
          // 1.31 — 8px a side on a phone, where every pixel of it is taken off
          // the width of the pages.
          ? 'flex-1 min-h-0 overflow-y-auto px-2 pt-4 sm:px-4 pb-[max(1rem,env(safe-area-inset-bottom))]'
          : cn('mx-auto pb-4', digital ? 'max-w-5xl' : 'max-w-3xl'),
      )}
    >
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={requestLeave}
          aria-label={t('common.back')}
          className="p-1.5 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
        >
          <Back className="h-5 w-5" />
        </button>
        <h1 className="text-base font-semibold truncate">{t('signing.new')}</h1>
        <div className="ms-auto text-sm text-muted-foreground text-end">
          {nextNumber.data && <div>{t('signing.numberBadge', { number: nextNumber.data.number })}</div>}
          <time>{new Intl.DateTimeFormat(he ? 'he-IL' : 'en-GB').format(new Date())}</time>
        </div>
      </div>

      {duplicateId && <p className="rounded-xl bg-primary/10 p-3 text-sm">{t('signing.duplicateNotice')}</p>}

      {/* --- what the document is called ------------------------------------
          1.16 — a row of its own, first on the page. It used to share the line
          with the recipient's name, which read as one answer in two halves and
          left the card below asking the same question again.

          The name is also the way into "my documents": a sender looks for a
          document by what it is called, so the shelf hangs off the name box
          rather than standing as a second control asking the same question. */}
      <div className="glass-card rounded-xl p-4">
        <Field htmlFor='compose-title' label={t('signing.documentName')} hint={!digital && library.data?.length ? t('signing.documentNameSearchHint') : undefined}>
          <DocumentTitleField
            id="compose-title"
            value={title}
            onChange={setTitle}
            documents={digital ? [] : (library.data ?? [])}
            onPick={(document) => { setTitle(document.title); setLibraryId(document.id); }}
            /* 1.23 — the boxes on the list and the button that adds the ticked
               ones. Not `libraryId`: that is one document's. */
            onPickMany={(documents) => void pickFromShelf(documents)}
            /* 1.29 — while a filed document is the pick (from the shelf, or from
               the address it was opened with), the X beside the name takes the
               pick back with it. §7 — the X is there for any name now, and
               without this only empties the box: on a digital document, where
               no file is showing, it never lets go of a pick nobody can see. */
            onCancel={fromLibrary && !digital ? cancelPick : undefined}
            desktop={desktop}
            /* §7 — a spinner while the shelf is on its way, and a way to ask again
               if it did not come. Not on a digital document, which offers no shelf. */
            loading={!digital && library.isFetching && !library.data}
            onRetry={!digital && library.isError && !library.isFetching ? () => void library.refetch() : undefined}
            placeholder={digital ? t('signing.digital.documentNameExample') : undefined}
          />
        </Field>
      </div>

      {/* --- the file, or the several that become one ----------------------
          1.22 — directly under the name, where the sender starts. It sat below
          the card that asks who signs, and said "choose files" until a file
          was picked, so the add button only existed once there was a file to
          add to. */}
      <div className="glass-card rounded-xl p-4">
        {/* The way back out of a digital document, to a file. The way in is
            below, beside "add a file": 1.16 took it off this card, and 1.22
            put it back at Nitzan's word. */}
        {digital && (
          <div className="flex flex-wrap gap-2 mb-3">
            <Button onClick={() => setDigital(false)}>{t('signing.digital.backToFile')}</Button>
          </div>
        )}
        {digital && <DigitalDocumentBuilder value={digitalDocument} onChange={setDigitalDocument} locale={locale} onPreview={() => setPreviewOpen(true)} />}
        {!digital && <>
        <input
          ref={inputRef}
          type="file"
          accept={COMPOSE_ACCEPT}
          multiple
          className="hidden"
          onChange={(event) => {
            const chosen = [...(event.target.files ?? [])];
            event.target.value = '';
            pick(chosen, replacing.current ? 'replace' : 'add');
            replacing.current = false;
          }}
        />

        {picked.length > 0 || preparing ? (
          <div className="space-y-3">
            {/* 1.30 — every file the document is made of, each with its own X at
                the end of its line: the top-left corner of it in Hebrew. It
                used to name the first and count the rest. */}
            <PickedFiles files={picked} busy={preparing} onRemove={removeFile} />
            <div className="flex items-center gap-3">
              <p className="min-w-0 flex-1 text-xs text-muted-foreground ltr-nums">
                {preparing
                  ? t('signing.converting')
                  : [
                      picked.length > 1 ? t('signing.filesMerged', { count: picked.length }) : null,
                      pageCount ? t('signing.filePages', { count: pageCount }) : null,
                    ]
                      .filter(Boolean)
                      .join(' · ') || t('common.loading')}
              </p>
              {/* Adding, not replacing: the second file is an appendix far more
                  often than it is a correction. Starting over is the other
                  button, and it says so. */}
              <Button
                size="sm"
                variant="ghost"
                icon={<Plus className="h-4 w-4" />}
                disabled={preparing}
                onClick={() => {
                  replacing.current = false;
                  inputRef.current?.click();
                }}
              >
                {t('signing.fileAdd')}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={preparing}
                onClick={() => {
                  replacing.current = true;
                  inputRef.current?.click();
                }}
              >
                {t('signing.fileReplace')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3 text-center">
            <p className="text-sm text-muted-foreground">{t('signing.fileHint')}</p>
            <div className="flex flex-wrap items-center justify-center gap-2">
              {/* 1.22 — "add a file" from the start, worded as it is once there
                  is one: there was nothing to add to, so it said "choose
                  files" until the first pick. */}
              <Button variant="primary" icon={<Upload className="h-4 w-4" />} onClick={() => inputRef.current?.click()}>
                {t('signing.fileAdd')}
              </Button>
              {/* Beside it: a document written here rather than brought. Only
                  in this state — with a file picked it would throw the file
                  away, and inside a digital document it is where you are. A
                  title picked from the shelf is let go, so the file it was
                  loading does not arrive over the document. */}
              <Button
                variant="secondary"
                icon={<FileText className="h-4 w-4" />}
                onClick={() => {
                  setDigital(true);
                  setLibraryId('');
                }}
              >
                {t('signing.digitalCreate')}
              </Button>
              {/* The other half of "where is the document": a permit or a form
                  handed over on paper has no file to choose, and photographing
                  it a page at a time is the only way it becomes one. */}

            </div>
          </div>
        )}

        {prepare.isError && <ErrorNotice code={(prepare.error as RequestError).code} />}
        </>}
      </div>

      {/* --- the pages, which are most of the screen ----------------------- */}
      {!digital && (file ? (
        /* 1.31 — 8px a side on a phone, where the page is as wide as what is left
           after the gutters of the card, the screen and the box it scrolls in. */
        <div className="glass-card rounded-xl p-2 sm:p-4">
          <div className="mb-3 space-y-2">
            <label className="flex min-h-11 items-center gap-3 text-sm">
              <input type="checkbox" checked={senderDetection} disabled={compose.isPending} onChange={event => setSenderDetection(event.target.checked)} />
              <span>{t('signing.senderDetection.enabled')}</span>
            </label>
            {detectionBusy && <p role="status" className="text-sm text-muted-foreground">{t('signing.senderDetection.scanning')}</p>}
            {!detectionBusy && detectionStatus && <p role="status" className="text-sm text-muted-foreground">{detectionStatus === 'found' ? t('signing.senderDetection.found', { count: detectedCount }) : detectionStatus === 'none' ? t('signing.senderDetection.none') : t('signing.senderDetection.failed')}</p>}
          </div>
          <DocumentPages
            src={file}
            /* A different file is a different document, drawn from scratch:
               same name and size and modified time is the same file. */
            refreshKey={`${file.name}:${file.size}:${file.lastModified}`}
            anchors={anchors}
            onAnchorsChange={setAnchors}
            onPages={setPageCount}
            label={t('signing.place')}
            signerCount={Math.max(signers.length, 1)}
            signerNames={signerNames}
            tall
          />
        </div>
      ) : (
        <div className="glass-card rounded-xl p-10 text-center text-sm text-muted-foreground">
          {preparing ? t('signing.converting') : t('signing.composeEmpty')}
        </div>
      ))}

      {/* --- everybody who signs it -----------------------------------------
          1.16 — the recipient is simply signer 1 in it. A digital document is
          filled in by the customer rather than signed on a page, so it shows
          the one row it can have and calls it delivery; a document with
          nobody here cannot be sent at all.

          1.24 — under the pages, where it sat above them, so that the pages
          follow the file directly. A digital document has no pages: its
          delivery still comes straight after the builder.

          1.27 — a document to sign has no heading and no line of explanation
          here: the rows are what the card is. It opens on the first signer's
          row, as the delivery card always did, where an empty card used to say
          "add at least one signer".

          1.30 — a row is the name and the phone on one line, and the address
          under them. The button that adds the next signer is the end of the
          first row's name line, where it had a header of its own above it. */}
      <RecipientChips signers={signers} onSave={(index,value)=>editSigner(index,value)} onRemove={dropSigner}/>

      {/* --- how it goes out ----------------------------------------------- */}
      <div className="glass-card rounded-xl border-2 border-primary/30 p-5 space-y-4">
        <div className="flex items-center justify-between gap-2">
          <span className="text-base font-semibold">{t('signing.sendChannels')}</span>
          {/* 1.57 — a secondary button, the icon with its name: on its own the
              icon was a 36px grey square and the one control here nobody found.
              Spelled out rather than a `Button`, which takes no ref, and the
              menu below is anchored to this element. */}
          <button
            ref={optionsRef}
            type="button"
            onClick={() => setOptionsOpen((was) => !was)}
            aria-expanded={optionsOpen}
            className="inline-flex min-h-11 items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-muted px-3 text-sm font-medium text-foreground transition-all hover:bg-accent active:scale-[0.97]"
          >
            <SlidersHorizontal className="h-4 w-4" />
            {t('signing.sendOptions')}
          </button>
        </div>

        <Field label={t('signing.sendFormat')}><Select value={format} onChange={value=>setFormat(value as typeof format)}><option value="link">{t('signing.sendFormatLink')}</option><option value="pdf">PDF</option><option value="both">{t('signing.sendFormatBoth')}</option></Select></Field>
        {/*
          Checkboxes rather than one choice: a customer who is chased on WhatsApp
          and keeps the paperwork in email wants both, and sending twice used to
          mean two documents with two numbers for one thing that was sent once.
        */}
        <div className="flex flex-wrap gap-2">
          {CHANNELS.map((option) => (
            <label
              key={option}
              className={cn(
                'flex flex-1 min-w-[9rem] min-h-14 cursor-pointer items-center gap-3 rounded-xl border-2 px-4 py-3 text-base font-semibold transition-colors',
                channels.includes(option)
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border text-muted-foreground hover:bg-muted',
              )}
            >
              <input
                type="checkbox"
                checked={channels.includes(option)}
                onChange={() => toggleChannel(option)}
                className="h-4 w-4 accent-primary"
              />
              {t(option === 'email' ? 'signing.channelEmail' : 'signing.channelWhatsapp')}
            </label>
          ))}
        </div>

        {channels.length === 0 && <p className="text-[11px] text-destructive">{t('signing.pickAChannel')}</p>}
        {/* Which address is missing, said once there is a document to send —
            not on an empty screen, where it is a complaint about a form nobody
            has started filling in yet. */}
        {(file || digital) && channels.some((channel) => addressOn(channel) === '') && (
          <p className="text-[11px] text-destructive">
            {t(
              channels.includes('email') && recipient.email.trim() === ''
                ? 'signing.needEmail'
                : 'signing.needPhone',
            )}
          </p>
        )}

        <AnchoredMenu open={optionsOpen} onClose={() => setOptionsOpen(false)} anchorRef={optionsRef} width={248}>
          <div className="p-2 space-y-3">
            {/*
              SIG-004 — how long the link stays open, on this document's own
              clock. A waiver signed at the door is dead by evening and a
              contract sent to a lawyer is not, and until now both expired on
              the platform's thirty days.
            */}
            <div className="space-y-1">
              <span className="px-1 text-[11px] font-semibold text-muted-foreground">{t('signing.validDays')}</span>
              <div className="grid grid-cols-4 gap-1">
                {SIGNATURE_VALID_DAYS.map((days) => (
                  <button
                    key={days}
                    type="button"
                    onClick={() => setValidDays(days)}
                    aria-pressed={validDays === days}
                    className={cn(
                      'h-8 rounded-lg text-xs font-medium ltr-nums transition-colors',
                      validDays === days
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground hover:bg-accent',
                    )}
                  >
                    {days}
                  </button>
                ))}
              </div>
              <p className="px-1 text-[11px] text-muted-foreground">
                {t(
                  validDays === LIMITS.signatureLinkTtlDays ? 'signing.validDaysDefault' : 'signing.validDaysOption',
                  { count: validDays },
                )}
              </p>
            </div>

            {/*
              The chase. Worki already wrote a follow-up when a document went
              unsigned, but on one threshold per business — the form somebody is
              standing at a counter waiting to sign was chased on the same clock
              as a contract sent for next month. Sending is the moment that
              difference is known, so it is asked here, next to the send.
            */}
            <div className="space-y-1 border-t border-border pt-2">
              <span className="px-1 text-[11px] font-semibold text-muted-foreground">{t('signing.followUp')}</span>
              {/*
                No chip for the business's own threshold: named, it read as one
                more length, and left blank it was a button with nothing on it.
                It is what is in force when none of these is on, so the chip
                that is on is turned off by pressing it again, and the line under
                them says which is in force.
              */}
              <div className="grid grid-cols-3 gap-1">
                {FOLLOW_UP_CHIPS.map((hours) => (
                  <button
                    key={hours}
                    type="button"
                    onClick={() => setFollowUpHours(followUpHours === hours ? null : hours)}
                    aria-pressed={followUpHours === hours}
                    className={cn(
                      'h-8 rounded-lg text-xs font-medium ltr-nums transition-colors',
                      followUpHours === hours
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground hover:bg-accent',
                    )}
                  >
                    {t('signing.hoursShort', { count: hours })}
                  </button>
                ))}
              </div>
              <FollowUpInput value={followUpHours} onChange={setFollowUpHours}/>
              <p className="px-1 text-[11px] text-muted-foreground">
                {followUpHours === null
                  ? t('signing.followUpDefault')
                  : t('signing.followUpEvery', { count: followUpHours })}
              </p>
            </div>
          </div>
        </AnchoredMenu>

        {digital && digitalDocument.blocks.length > 0 && !digitalDocumentSchema.safeParse(digitalDocument).success && <p role="alert" className="text-sm text-destructive">{t("validation.digitalDocument.structure")}</p>}
        {compose.isError && <ErrorNotice code={(compose.error as RequestError).code} />}

        {digital && <Button block className="min-h-12" onClick={() => setPreviewOpen(true)}><Eye size={18}/>{t('signing.digital.previewBeforeSend')}</Button>}
        <Button
          variant="primary"
          block
          loading={compose.isPending && compose.variables !== true}
          disabled={!ready || compose.isPending || detectionBusy || pendingSenderRegions !== null}
          onClick={() => compose.mutate(false)}
        >
          {t('signing.send')}
        </Button>
        {/* SIG-3.13 — beside the send rather than behind a menu. The reason to
            press it is that something has come up, and a sender who has to stop
            should be able to stop in one press. */}
        <Button
          block
          className="min-h-12"
          icon={<Save className="h-4 w-4" />}
          loading={compose.isPending && compose.variables === true}
          disabled={!keepable || compose.isPending || detectionBusy || pendingSenderRegions !== null}
          onClick={() => compose.mutate(true)}
        >
          {t('signing.saveDraft')}
        </Button>
      </div>

      {digital && previewOpen && <DigitalDocumentPreview document={digitalDocument} title={title} number={nextNumber.data?.number} business={business} locale={locale} onClose={() => setPreviewOpen(false)} />}
      <ConfirmDialog
        open={leaving}
        title={t('signing.leaveTitle')}
        message={t('signing.leaveBody')}
        confirmLabel={t('common.back')}
        onConfirm={leave}
        onCancel={() => setLeaving(false)}
      />

      {/* The scan comes back as a PDF, so it goes through `pick` like any other
          chosen file — nothing downstream of here knows it was photographed. */}
      {pendingSenderRegions && pendingSenderRegions.file === file && senderDetection && <SenderSignatureDialog
        onClose={() => { setPendingSenderRegions(null); setDetectedCount(0); setDetectionStatus(null); }}
        onSave={image => {
          const found = pendingSenderRegions;
          if (found.file !== detectionFileNow.current || !senderDetectionNow.current) return;
          const merged = mergeSenderAnchors(anchorsNow.current, found.anchors, image);
          setAnchors(signAsSender(merged.anchors, image));
          setDetectedCount(merged.added); setDetectionStatus('found');
          setPendingSenderRegions(null);
        }}
      />}

    </div>
  );

  /*
   * SIG-3.12 — over the list, not instead of it. The screen keeps its own
   * header, its own pages and its own send bar; what the shell adds is the
   * dialog around them, and the list behind stays exactly where it was.
   *
   * The widest there is. The digital builder puts a form beside a live preview
   * and has one of the two turn into a gutter at the narrower size; 1.31 — the
   * pages of an uploaded file are drawn as wide as the sheet leaves them, so a
   * narrower sheet is a smaller page.
   */
  return asDialog ? (
    <ModalShell open label={t('signing.new')} onClose={requestLeave} size="widest">
      {screen}
    </ModalShell>
  ) : (
    screen
  );
}
